import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_MODE,
  EXPERIENCE_STORAGE_KEY,
  VALID_MODES,
  getModeThemeConfig,
  isValidExperienceMode,
  modeThemeConfig,
} from "../config/mode-theme.config";

const ExperienceContext = createContext();

function getInitialMode() {
  if (typeof window === "undefined") {
    return DEFAULT_MODE;
  }

  const savedMode = window.localStorage.getItem(EXPERIENCE_STORAGE_KEY);

  if (isValidExperienceMode(savedMode)) {
    return savedMode;
  }

  return DEFAULT_MODE;
}

function applyModeToBody(mode) {
  if (typeof document === "undefined") return;

  const config = getModeThemeConfig(mode);

  VALID_MODES.forEach((validMode) => {
    const modeConfig = getModeThemeConfig(validMode);

    if (modeConfig.bodyClass) {
      document.body.classList.remove(modeConfig.bodyClass);
    }
  });

  if (config.bodyClass) {
    document.body.classList.add(config.bodyClass);
  }

  document.body.setAttribute("data-experience", mode);
}

function getRevealRadius(x, y) {
  const width = window.innerWidth;
  const height = window.innerHeight;

  const maxX = Math.max(x, width - x);
  const maxY = Math.max(y, height - y);

  return Math.hypot(maxX, maxY);
}

function createTransitionOverlay({ x, y, color, glow, radius }) {
  const overlay = document.createElement("div");

  overlay.className = "manual-view-transition";
  overlay.style.setProperty("--vt-x", `${x}px`);
  overlay.style.setProperty("--vt-y", `${y}px`);
  overlay.style.setProperty("--vt-radius", `${radius}px`);
  overlay.style.setProperty("--vt-color", color);
  overlay.style.setProperty("--vt-glow", glow);

  document.body.appendChild(overlay);

  requestAnimationFrame(() => {
    overlay.classList.add("is-entering");
  });

  return overlay;
}

function normalizeItemModes(item) {
  if (!item) return [];

  if (Array.isArray(item.modes)) {
    return item.modes.filter(Boolean);
  }

  if (typeof item.mode === "string" && item.mode.trim()) {
    if (item.mode === "both") {
      return ["aesthetics", "nutrition"];
    }

    return [item.mode];
  }

  return [];
}

export function ExperienceProvider({ children }) {
  const [mode, setMode] = useState(getInitialMode);

  useEffect(() => {
    applyModeToBody(mode);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(EXPERIENCE_STORAGE_KEY, mode);
    }
  }, [mode]);

  const matchesMode = (item) => {
    if (!item) return true;

    const itemModes = normalizeItemModes(item);

    if (!itemModes.length || mode === "all") {
      return true;
    }

    return itemModes.includes(mode);
  };

  const filterByMode = (items = []) => {
    return items.filter(matchesMode);
  };

  const setModeWithTransition = (nextMode, origin) => {
    if (!isValidExperienceMode(nextMode) || nextMode === mode) return;

    const nextTheme = getModeThemeConfig(nextMode);
    const defaultTheme = getModeThemeConfig(DEFAULT_MODE);

    const safeOrigin = origin || {
      x: window.innerWidth * 0.08,
      y: window.innerHeight * 0.5,
    };

    const radius = getRevealRadius(safeOrigin.x, safeOrigin.y);

    const overlay = createTransitionOverlay({
      x: safeOrigin.x,
      y: safeOrigin.y,
      radius,
      color: nextTheme.overlay || defaultTheme.overlay,
      glow: nextTheme.glow || defaultTheme.glow,
    });

    window.setTimeout(() => {
      setMode(nextMode);
    }, 70);

    window.setTimeout(() => {
      overlay.classList.add("is-leaving");
    }, 720);

    window.setTimeout(() => {
      overlay.remove();
    }, 1100);
  };

  const value = useMemo(() => {
    const modeTheme = getModeThemeConfig(mode);
    const defaultTheme = getModeThemeConfig(DEFAULT_MODE);

    return {
      mode,
      setMode,
      setModeWithTransition,

      matchesMode,
      filterByMode,

      modeLabel: modeTheme.label || defaultTheme.label,
      modeTheme,

      modes: VALID_MODES,
      modeThemeConfig,
    };
  }, [mode]);

  return (
    <ExperienceContext.Provider value={value}>
      {children}
    </ExperienceContext.Provider>
  );
}

export function useExperience() {
  const context = useContext(ExperienceContext);

  if (!context) {
    throw new Error("useExperience must be used within an ExperienceProvider");
  }

  return context;
}